import { useCallback, useMemo, useRef, useState } from "react";

/**
 * SIMS-8614 — state for a full-page configuration editor (the Pattern v2 editor shell).
 *
 * Hand-off §2:
 *   rule 2 — breadcrumb, sticky Cancel/Save header, an "Unsaved changes" pill when dirty
 *   rule 3 — Cancel, breadcrumb, navigating away AND tab switches all route through ONE prompt
 *   rule 4 — save disabled while in flight; on failure the form keeps the input and says why
 *
 * DECISION, because no source settles it: **dirty means the draft differs from the saved record**,
 * not "a field has been touched". TestRail C17559 only asks that reverting a change behave
 * "predictably". Differs-from-saved is the honest signal — the pill means exactly one thing, and
 * AC2's prompt fires only when something would really be lost.
 *
 * AC4 ("nothing is half-saved") is transactional at the service, since the payer save is a single
 * request. What this hook guarantees is that a FAILED save is never treated as saved: the snapshot
 * only advances on success, so the draft stays dirty and reopening the record shows saved values.
 *
 * Reused by SIMS-8607 (pattern tester), SIMS-8653 (mode notice) and SIMS-8634 (categories), so it
 * carries the chrome state and leaves the fields to the caller.
 */

/** Which affordance asked to leave — the prompt is shared, the destination is not. */
export type ExitRoute = "cancel" | "breadcrumb" | "navigate" | "tab";

export interface UseConfigEditorOptions<T> {
	/** The saved record. Also the baseline for dirty comparison and for discard. */
	initial: T;
	onSave: (draft: T) => Promise<void>;
	/** Fields compared after trimming, so trailing whitespace is not a "change". */
	trimmedFields?: (keyof T)[];
}

export interface UseConfigEditorResult<T> {
	draft: T;
	setField: <K extends keyof T>(key: K, value: T[K]) => void;
	/** Replace the whole draft — for a multi-field control such as the category picker. */
	setDraft: (next: T) => void;
	isDirty: boolean;
	/** The last successfully saved values. Advances only on a successful save. */
	savedSnapshot: T;
	isSaving: boolean;
	canSave: boolean;
	saveError: string | null;
	save: () => Promise<void>;
	discardPromptOpen: boolean;
	exitRoute: ExitRoute | null;
	/** Ask to leave. Clean → proceeds immediately; dirty → opens the prompt. */
	requestExit: (proceed: () => void, route?: ExitRoute) => void;
	keepEditing: () => void;
	discard: () => void;
}

const normalise = <T>(value: T, trimmed: boolean): unknown =>
	trimmed && typeof value === "string" ? value.trim() : value;

export function useConfigEditor<T extends Record<string, unknown>>({
	initial,
	onSave,
	trimmedFields,
}: UseConfigEditorOptions<T>): UseConfigEditorResult<T> {
	const [savedSnapshot, setSavedSnapshot] = useState<T>(initial);
	const [draft, setDraftState] = useState<T>(initial);
	const [isSaving, setIsSaving] = useState(false);
	const [saveError, setSaveError] = useState<string | null>(null);
	const [discardPromptOpen, setDiscardPromptOpen] = useState(false);
	const [exitRoute, setExitRoute] = useState<ExitRoute | null>(null);
	// Held in a ref, not state: the pending destination must survive the prompt's renders and
	// must not itself trigger one.
	const pendingExitRef = useRef<(() => void) | null>(null);
	// Guards a double submit at the source. A disabled button is not enough — Enter in a text
	// field, or a second click landing in the same tick, both bypass it (AC of SIMS-8607 too).
	const savingRef = useRef(false);

	const setField = useCallback(<K extends keyof T>(key: K, value: T[K]) => {
		setDraftState((prev) => ({ ...prev, [key]: value }));
	}, []);

	const setDraft = useCallback((next: T) => setDraftState(next), []);

	const isDirty = useMemo(() => {
		const keys = new Set([
			...Object.keys(savedSnapshot),
			...Object.keys(draft),
		]);
		// Every field is compared trimmed by default: a stray space is not a change the user
		// would recognise as one, and treating it as one makes the pill lie.
		const shouldTrim = (k: string) =>
			trimmedFields === undefined || trimmedFields.includes(k as keyof T);
		return Array.from(keys).some(
			(k) =>
				normalise(draft[k as keyof T], shouldTrim(k)) !==
				normalise(savedSnapshot[k as keyof T], shouldTrim(k)),
		);
	}, [draft, savedSnapshot, trimmedFields]);

	const save = useCallback(async () => {
		if (savingRef.current || !isDirty) {
			// Nothing to send: either a save is already in flight, or the draft matches the
			// saved record and a request would be a no-op the audit log would still record.
			return;
		}
		savingRef.current = true;
		setIsSaving(true);
		setSaveError(null);
		try {
			await onSave(draft);
			// Advance the baseline ONLY here — this is what makes a failed save non-destructive.
			setSavedSnapshot(draft);
		} catch (error: unknown) {
			const message =
				error instanceof Error && error.message !== ""
					? error.message
					: "The payer could not be saved.";
			setSaveError(message);
		} finally {
			savingRef.current = false;
			setIsSaving(false);
		}
	}, [draft, isDirty, onSave]);

	const requestExit = useCallback(
		(proceed: () => void, route: ExitRoute = "cancel") => {
			if (!isDirty) {
				proceed();
				return;
			}
			pendingExitRef.current = proceed;
			setExitRoute(route);
			setDiscardPromptOpen(true);
		},
		[isDirty],
	);

	const keepEditing = useCallback(() => {
		// AC2: everything stays exactly as it was — the draft is untouched here on purpose.
		pendingExitRef.current = null;
		setExitRoute(null);
		setDiscardPromptOpen(false);
	}, []);

	const discard = useCallback(() => {
		setDraftState(savedSnapshot);
		setSaveError(null);
		setDiscardPromptOpen(false);
		setExitRoute(null);
		const proceed = pendingExitRef.current;
		pendingExitRef.current = null;
		proceed?.();
	}, [savedSnapshot]);

	return {
		draft,
		setField,
		setDraft,
		isDirty,
		savedSnapshot,
		isSaving,
		canSave: isDirty && !isSaving,
		saveError,
		save,
		discardPromptOpen,
		exitRoute,
		requestExit,
		keepEditing,
		discard,
	};
}
